// ============================================
// CRÉATION MANUELLE D'UN DOSSIER (appel reçu, visite…)
// ============================================
// POST /admin/tickets : titre, description, type, priorité, et soit un client
// existant (customerId), soit un contact libre (nom, e-mail, téléphone,
// société) ; équipement facultatif.

import { IssueType, TicketPriority, type CreateTicketPayload } from '../../types';

export interface SaisieNouveauDossier {
  customerId: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  companyName: string;
  title: string;
  issueType: IssueType;
  priority: TicketPriority;
  description: string;
  equipmentBrand: string;
  equipmentModel: string;
  serialNumber: string;
  errorCode: string;
}

export const SAISIE_VIDE: SaisieNouveauDossier = {
  customerId: '', contactName: '', contactEmail: '', contactPhone: '', companyName: '',
  title: '', issueType: IssueType.TECHNICAL, priority: TicketPriority.MEDIUM, description: '',
  equipmentBrand: '', equipmentModel: '', serialNumber: '', errorCode: '',
};

/** Le serveur exige un titre de 5 à 200 caractères. */
export const TITRE_MIN = 5;
export const TITRE_MAX = 200;
/** Le serveur accepte 10 000 caractères de description. */
export const DESCRIPTION_MAX = 10000;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Erreurs de saisie, par champ ; objet vide si le formulaire peut partir. */
export function verifierNouveauDossier(s: SaisieNouveauDossier): Partial<Record<keyof SaisieNouveauDossier, string>> {
  const erreurs: Partial<Record<keyof SaisieNouveauDossier, string>> = {};
  const titre = s.title.trim();
  if (titre.length < TITRE_MIN) erreurs.title = `Le titre doit faire au moins ${TITRE_MIN} caractères.`;
  else if (titre.length > TITRE_MAX) erreurs.title = `Le titre ne doit pas dépasser ${TITRE_MAX} caractères.`;
  if (s.description.length > DESCRIPTION_MAX) erreurs.description = `La description ne doit pas dépasser ${DESCRIPTION_MAX} caractères.`;
  if (!s.customerId) {
    if (s.contactEmail.trim() && !EMAIL.test(s.contactEmail.trim())) erreurs.contactEmail = 'Adresse e-mail invalide.';
    if (!s.contactName.trim() && !s.contactEmail.trim() && !s.contactPhone.trim() && !s.companyName.trim()) {
      erreurs.contactName = 'Choisissez un client ou renseignez au moins un moyen de contact.';
    }
  }
  return erreurs;
}

/** Saisie → corps de la requête : champs vides retirés, client existant prioritaire sur le contact libre. */
export function versPayloadCreation(s: SaisieNouveauDossier): CreateTicketPayload {
  const payload: CreateTicketPayload = {
    title: s.title.trim(),
    issueType: s.issueType,
    priority: s.priority,
  };
  const ajouter = (cle: keyof CreateTicketPayload, valeur: string) => {
    const v = valeur.trim();
    if (v) (payload as unknown as Record<string, string>)[cle] = v;
  };
  ajouter('description', s.description);
  if (s.customerId) {
    payload.customerId = s.customerId;
  } else {
    ajouter('contactName', s.contactName);
    ajouter('contactEmail', s.contactEmail);
    ajouter('contactPhone', s.contactPhone);
    ajouter('companyName', s.companyName);
  }
  ajouter('equipmentBrand', s.equipmentBrand);
  ajouter('equipmentModel', s.equipmentModel);
  ajouter('serialNumber', s.serialNumber);
  ajouter('errorCode', s.errorCode);
  return payload;
}
